/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import { Link, Page, Text, View } from "@react-pdf/renderer";
import { IPDFProgramDetailsPage } from "./PDFTypes";
import * as PDFStyles from "./PDFStyles";
import * as PDFUtils from "@utils/pdfUtils";
import { IUserProgramCertificate, ProgramCertificate } from "@interfaces/Interfaces";
import AWSService from "@controllers/AWSService";
import { format } from "date-fns";
import * as ProgramUtils from "@utils/programUtils";

const PDFProgramDetailsPage: React.FC<IPDFProgramDetailsPage> = (props) => {
  const userRoles = props.userRoles as string[] | { roleID: string; roleName: string; optionalStandards: any[] }[];
  let roles: string[] = [];

  if (userRoles && typeof userRoles[0] === "string") {
    roles = userRoles as string[];
  } else if (userRoles !== undefined && userRoles.length > 0) {
    roles = (userRoles as { roleID: string; roleName: string; optionalStandards: any[] }[]).map(
      (role) => role.roleName
    );
  }

  return (
    <>
      {props.program ? (
        <Page
          size="A4"
          style={PDFStyles.styles.page}
        >
          <View
            style={PDFStyles.styles.heading}
            fixed
          >
            <Text style={PDFStyles.styles.headingText}>{"ParaFolio"}</Text>
          </View>
          <View
            fixed
            style={PDFStyles.styles.pageHeader}
            render={({ pageNumber }) => (
              <>
                <Text style={PDFStyles.styles.pageHeaderText}>{"Details"}</Text>
                <Text style={PDFStyles.styles.pageNumber}>{pageNumber - 1}</Text>
              </>
            )}
          />
          <View style={PDFStyles.styles.pageInnerContainer}>
            <Text
              style={[PDFStyles.styles.standardsHeaderOverview, { marginBottom: 0 }]}
            >{`${props.program.Title} Details`}</Text>
            <View style={PDFStyles.styles.separator} />
            {props.user.name && (
              <View>
                <Text style={PDFStyles.styles.reflectionHeader}>{"Name:"}</Text>
                <Text style={PDFStyles.styles.evidenceDate}>{props.user.name}</Text>
              </View>
            )}
            {props.user.email && (
              <View>
                <Text style={PDFStyles.styles.reflectionHeader}>{"Email:"}</Text>
                <Link
                  style={PDFStyles.styles.hyperlink}
                  src={`mailto:${props.user.email}`}
                >
                  {props.user.email}
                </Link>
              </View>
            )}
            {roles.length > 0 && (
              <View>
                <Text style={PDFStyles.styles.reflectionHeader}>{"Roles:"}</Text>
                {roles.sort().map((role: string) => (
                  <Text
                    key={role}
                    style={[PDFStyles.styles.detailsInfo, { marginBottom: 4 }]}
                  >
                    {role}
                  </Text>
                ))}
              </View>
            )}
            {props.userLevel && (
              <View>
                <Text style={PDFStyles.styles.reflectionHeader}>{"Level:"}</Text>
                <Text style={PDFStyles.styles.evidenceDate}>{props.userLevel}</Text>
              </View>
            )}
            {props.secondaryRoles && props.secondaryRoles.length > 0 && (
              <View>
                <Text style={PDFStyles.styles.reflectionHeader}>
                  {props.secondaryRoles.length === 1 ? "Secondary role:" : "Secondary roles:"}
                </Text>
                {props.secondaryRoles.sort().map((role: string) => (
                  <Text
                    key={role}
                    style={[PDFStyles.styles.detailsInfo, { marginBottom: 4 }]}
                  >
                    {role}
                  </Text>
                ))}
              </View>
            )}
            {props.userArea && (
              <View>
                <Text style={PDFStyles.styles.reflectionHeader}>{"Area:"}</Text>
                <Text style={PDFStyles.styles.evidenceDate}>{props.userArea}</Text>
              </View>
            )}
            {props.userTeam && (
              <View>
                <Text style={PDFStyles.styles.reflectionHeader}>{"Team:"}</Text>
                <Text
                  key={props.userTeam}
                  style={[PDFStyles.styles.detailsInfo, { marginBottom: 4 }]}
                >
                  {props.userTeam}
                </Text>
              </View>
            )}
            {props.courseId && props.courseId !== "" && (
              <View>
                <Text style={PDFStyles.styles.reflectionHeader}>{"Course Id:"}</Text>
                <Text
                  key={props.courseId}
                  style={[PDFStyles.styles.detailsInfo, { marginBottom: 4 }]}
                >
                  {props.courseId}
                </Text>
              </View>
            )}
            <View>
              <Text style={PDFStyles.styles.reflectionHeader}>{"ESR No:"}</Text>
              <Text style={PDFStyles.styles.evidenceDate}>
                {PDFUtils.getUserESRNumber(props.user.userData?.subscriptions, props.program.Subscription)}
              </Text>
            </View>
            {!PDFUtils.hideUserRegions(props.program.ID) && (
              <View>
                <Text style={PDFStyles.styles.reflectionHeader}>{"Regions:"}</Text>
                <Text style={PDFStyles.styles.evidenceDate}>
                  {PDFUtils.getUserRegions(props.user.userData?.Regions)}
                </Text>
              </View>
            )}
            {props.programCertificates.length > 0 && (
              <View>
                <Text style={PDFStyles.styles.reflectionHeader}>{"Certificates:"}</Text>
                {props.programCertificates.sort().map((_certificate: IUserProgramCertificate) => {
                  const programCertificate: ProgramCertificate = props.allProgramCertificates.find(
                    (item: ProgramCertificate) =>
                      item.Name.toUpperCase() === _certificate?.certificate?.certificateType.toUpperCase()
                  );

                  // Special condition for SWAST EPRR to hide legacy supporting evidence certificate
                  if (
                    props.program &&
                    ProgramUtils.isSwastEPRR(props.program.ID) &&
                    _certificate.programData.certificateType === "Supporting Evidence (Mandatory)"
                  ) {
                    return "";
                  }

                  const signedURL = AWSService.getCertificateURL(props.user, props.accessParams, _certificate);

                  const isVerifiedCertificate =
                    _certificate.verificationStatus?.status === "Verified" &&
                    _certificate.verificationStatus?.verificationDate;

                  return (
                    <View key={_certificate?.certificate?.certificateType || _certificate.certificate?.name}>
                      <Text style={PDFStyles.styles.reflectionHeader}>
                        {PDFUtils.getCertificateHeader(_certificate, programCertificate, props.program)}
                      </Text>
                      <Link
                        style={PDFStyles.styles.hyperlink}
                        src={signedURL}
                        key={_certificate.certificate?.name}
                      >
                        {_certificate.certificate?.name}
                      </Link>
                      {_certificate.issueDate && (
                        <View style={PDFStyles.styles.certificateDate}>
                          <Text style={PDFStyles.styles.boldHeader}>{"Issue Date: "}</Text>
                          <Text style={PDFStyles.styles.evidenceDate}>
                            {format(new Date(_certificate.issueDate), "d MMMM yyyy")}
                          </Text>
                        </View>
                      )}
                      {isVerifiedCertificate && (
                        <>
                          <View style={PDFStyles.styles.certificateDate}>
                            <Text style={PDFStyles.styles.boldHeader}>{"Verified By: "}</Text>
                            <Text style={PDFStyles.styles.evidenceDate}>
                              {_certificate.verificationStatus?.trainerName}
                            </Text>
                          </View>
                          <View style={PDFStyles.styles.certificateDate}>
                            <Text style={PDFStyles.styles.boldHeader}>{"Verified Date: "}</Text>
                            <Text style={PDFStyles.styles.evidenceDate}>
                              {format(new Date(_certificate.verificationStatus?.verificationDate!), "d MMMM yyyy")}
                            </Text>
                          </View>
                        </>
                      )}
                      {_certificate.expiryDate && (
                        <View style={PDFStyles.styles.certificateDate}>
                          <Text style={PDFStyles.styles.boldHeader}>{"Expiry Date: "}</Text>
                          <Text style={PDFStyles.styles.evidenceDate}>
                            {format(new Date(_certificate.expiryDate), "d MMMM yyyy")}
                          </Text>
                        </View>
                      )}
                    </View>
                  );
                })}
              </View>
            )}
          </View>
        </Page>
      ) : null}
    </>
  );
};

export default PDFProgramDetailsPage;
